import { IconButton } from '@mui/material'
import {
  ComponentProps,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
} from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import IcBaselineClose from '~icons/ic/baseline-close'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import type { UserSubscriptionBaseFragment } from 'src/services/apollo/generated/graphql'
import dayjs from 'src/utils/dayjs'
import { navigateToNativePage } from 'src/utils/jsBridge/implementation'
import { useJSBridgeFn } from 'src/utils/jsBridge/jsbridgeHook'
import { NativeNavigation } from 'src/utils/jsBridge/nativeNavigation'
import { track } from 'src/utils/tracker'
import { isAppWebview } from 'src/utils/userAgent'
import {
  GrowthEventName,
  useGrowthEvent,
} from '../../features/GrowthEvent/store'
import { AppDownloadButtons } from '../AppDownload'
import { UserCancelled } from '../Modal/Confirm'
import { UIButton } from '../ui/Button'
import Text from '../ui/Text'
import { openMembershipSubscriptionSurveyDialog } from './MembershipCancelSurveys'
import { MembershipIconCircled } from './MembershipIcon'

function PaymentFailedDialogContent(
  props: PropsWithChildren<{
    close: () => any
  }>,
) {
  const { close, children } = props
  const { t } = useTranslation(['membership', 'action'])

  return (
    <>
      <UIDialogHeader
        heading={t('membership:payment.sth-went-wrong')}
        className="pr-10"
      >
        {({ heading }) => (
          <>
            {heading}
            <IconButton className="absolute top-3 right-3" onClick={close}>
              <IcBaselineClose className="size-6" />
            </IconButton>
          </>
        )}
      </UIDialogHeader>
      <UIDialogContent>
        {children}
        <div className="my-4 w-full h-px bg-zinc-300 dark:bg-zinc-500"></div>
        <div>
          <p className="font-semibold text-lg">
            {t('membership:payment.failed.dialog.try-our-app-title')}
          </p>
          <AppDownloadButtons
            className="mt-3 [&_h2]:text-base"
            // Temporary: hide Android in the payment failed dialog only.
            // Remove this prop to restore the Android download option.
            hideAndroid
            hideAndroidFilterFree
            trackingProps={{
              location: 'payment-failed',
            }}
          />
        </div>
      </UIDialogContent>
      <UIDialogFooter>
        <UIButton onPress={close}>{t('action:cancel')}</UIButton>
      </UIDialogFooter>
    </>
  )
}

export const openPaymentFailedDialog = (errorContent: ReactNode) => {
  UIDialog.create({
    className:
      'relative w-fit max-w-[calc(100vw-32px)] msm:w-full msm:max-w-[800px]',
    children: ({ close }) => (
      <PaymentFailedDialogContent close={() => close()}>
        {errorContent}
      </PaymentFailedDialogContent>
    ),
  })
}

function CancelSubscriptionContent(props: {
  subscription?: UserSubscriptionBaseFragment
  tier?: number
  close: () => any
  onSuccess: () => any
  onError: () => any
}) {
  const { t } = useTranslation(['membership', 'action'])
  const { close, onSuccess, onError, subscription, tier } = props
  return (
    <>
      <UIDialogHeader
        heading={t('membership:plan.action.cancel.confirm-dialog.title')}
      />
      <UIDialogContent>
        <p>{t('membership:plan.action.cancel.confirm-dialog.content')}</p>
        <p className="text-sm text-theme-primary">
          {t('membership:plan.action.cancel.confirm-dialog.expires-at')}:{' '}
          {dayjs(subscription?.endAt).format('l')}
        </p>
      </UIDialogContent>
      <UIDialogFooter>
        <UIButton
          onPress={() => {
            close()
            onError()
          }}
        >
          {t('action:cancel')}
        </UIButton>
        <UIButton
          variant="filled"
          busy="auto"
          onPress={async () => {
            track('plan_cancel_confirm', {
              type: subscription?.interval,
              tier,
            })
            close()
            onSuccess()
            // Open Cancel Subscription Survey
            await openMembershipSubscriptionSurveyDialog({
              type: 'cancel-subs',
            })
          }}
        >
          {t('membership:plan.action.cancel.confirm-dialog.confirm-label')}
        </UIButton>
      </UIDialogFooter>
    </>
  )
}

export function openCancelSubscriptionDialog(
  subscription?: UserSubscriptionBaseFragment,
  tier?: number,
) {
  return new Promise<void>((resolve, reject) => {
    UIDialog.create({
      dismissable: true,
      className: 'max-w-screen-msm',
      children: ({ close }) => (
        <CancelSubscriptionContent
          subscription={subscription}
          close={() => close()}
          onSuccess={() => {
            resolve()
          }}
          onError={() => {
            reject(UserCancelled)
          }}
          tier={tier}
        />
      ),
      onClose: () => {
        reject(UserCancelled)
      },
    })
  })
}

function MembershipRequiredDialogContent(props: {
  feature: string
  content?: string
  close: () => void
}) {
  const navigate = useNavigate()

  const navigateToMembershipPage = useJSBridgeFn(
    () => {
      navigate('/membership/plans')
    },
    () => {
      navigateToNativePage({
        route: NativeNavigation.membership('subscription'),
      })
    },
  )

  const isWebView = isAppWebview()

  const onConfirm = useCallback(() => {
    navigateToMembershipPage()

    if (isWebView) return

    props.close?.()
  }, [isWebView, navigateToMembershipPage, props])

  const { ongoing } = useGrowthEvent(GrowthEventName.AugustEvent2025)

  const { t } = useTranslation('membership')

  return (
    <>
      <UIDialogHeader
        heading={t('membership:membership-requirement.title')}
        className="flex items-center gap-2"
      >
        {({ heading }) => (
          <>
            <MembershipIconCircled className="size-6 -my-1" />
            {heading}
          </>
        )}
      </UIDialogHeader>
      <UIDialogContent>
        {props.content === undefined ? (
          <Text
            i18nKey={`membership:membership-requirement.content.${props.feature}`}
          />
        ) : (
          props.content
        )}
        {ongoing && (
          <Link className="text-amber-600 font-bold" to="/membership/plans">
            <Text i18nKey="membership:membership-requirement.content.august-event" />
          </Link>
        )}
      </UIDialogContent>
      <UIDialogFooter>
        <UIButton onPress={props.close}>
          <Text i18nKey="action:cancel" />
        </UIButton>
        <UIButton variant="filled" onPress={onConfirm}>
          <Text i18nKey="membership:membership-requirement.action.become-member" />
        </UIButton>
      </UIDialogFooter>
    </>
  )
}

export async function openMembershipRequiredDialog(
  feature: string,
  content?: string,
  onClose?: () => void,
) {
  await UIDialog.create({
    className: 'max-w-screen-msm',
    children: ({ close }) => (
      <MembershipRequiredDialogContent
        feature={feature}
        content={content}
        close={() => {
          onClose?.()
          close()
        }}
      />
    ),
  })
}
